import { useState, useContext, useEffect, useMemo } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import Pagination from "../../components/Pagination";
import Search from '../../components/Search';
import ChargesTable from './ChargesTable';

const Charges = () => {
    const { chargesData, filterChargesData, setFilterChargesData } = useContext(GlobalContext);

    const [currentPage, setCurrentPage] = useState(1);
    const [postsPerPage, setPostsPerPage] = useState(10);
    const [selectedIds, setSelectedIds] = useState([]);
    const [fileCount, setFileCount] = useState(0);
    const [totalWords, setTotalWords] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);

    const filteredData = Array.isArray(filterChargesData) ? filterChargesData : [];

    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;

    const currentPosts = useMemo(
        () => filteredData.slice(indexOfFirstPost, indexOfLastPost),
        [filteredData, indexOfFirstPost, indexOfLastPost]
    );

    const howManyPages = useMemo(
        () => Math.ceil(filteredData.length / postsPerPage),
        [filteredData.length, postsPerPage]
    );

    const showAlert = (message) => {
        alert(message); // Can be replaced with toast
    };

    const handleCheckboxChange = (id) => {
        setSelectedIds(prev => {
            const alreadySelected = prev.includes(id);
            const updated = alreadySelected
                ? prev.filter(selectedId => selectedId !== id)
                : [...prev, id];

            showAlert(alreadySelected ? "Unchecked file." : "File selected.");
            return updated;
        });
    };

    const handleSelectAll = () => {
        const currentPageIds = currentPosts.map(item => item.id);
        const allSelected = currentPageIds.every(id => selectedIds.includes(id));

        setSelectedIds(prev => {
            const updated = allSelected
                ? prev.filter(id => !currentPageIds.includes(id))
                : [...new Set([...prev, ...currentPageIds])];

            // showAlert(allSelected ? "Deselected all on this page." : "Selected all on this page.");
            return updated;
        });
    };

    const checkAll = () => {
        const allIds = filteredData.map(item => item.id);
        const allSelected = allIds.every(id => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds([]);
            showAlert("✅ All files unchecked.");
        } else {
            setSelectedIds(allIds);
            showAlert("✅ All files selected.");
        }
    };

    const handleRemoveText = (indexToRemove) => {
        setSelectedIds(prev =>
            prev.filter((_, index) => index !== indexToRemove)
        );
    };

    // ✅ Calculate word/amount counts on selection change
    useEffect(() => {
        let wordCount = 0;
        let amountSum = 0;
        const selectedSet = new Set(selectedIds);
        const dataMap = new Map(filteredData.map(item => [item.id, item]));

        selectedSet.forEach(id => {
            const item = dataMap.get(id);
            if (item) {
                wordCount += parseInt(item.wordcounts || 0);
                const amount = parseInt(item.amount || 0);
                if (!isNaN(amount)) amountSum += amount;
            }
        });

        setTotalWords(wordCount);
        setFileCount(selectedSet.size);
        setTotalAmount(amountSum);
    }, [selectedIds, filteredData]);

    // ✅ Reset on search/filter change
    useEffect(() => {
        setSelectedIds([]);
        setFileCount(0);
        setTotalWords(0);
        setTotalAmount(0);
    }, [filterChargesData]);

    return (
        <>
            <div className="container mb-5">
                <div className="mb-1 mt-2">
                    <div className="row">
                        <div className="col-md-6">
                            <Search data={chargesData} setFilterData={setFilterChargesData} />
                        </div>
                        <div className="col-md-6 mt-2">
                            <div>
                                <span className="border bg-primary text-white mx-2 p-2 link_text">
                                    File: {fileCount}
                                </span>
                                <span className="border bg-primary text-white mx-2 p-2 link_text">
                                    Words Count: {totalWords}
                                </span>
                                <span className="border bg-primary text-white mx-2 p-2 link_text">
                                    Amount: {totalAmount}
                                </span>
                                <button
                                    className="btn btn-sm btn-outline-primary ms-3"
                                    onClick={checkAll}
                                >
                                    ✅ Check All
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {currentPosts.length > 0 ? (
                    <div>
                        <ChargesTable
                            currentPosts={currentPosts}
                            handleCheckboxChange={handleCheckboxChange}
                            selectedIds={selectedIds}
                            handleSelectAll={handleSelectAll}
                        />
                    </div>
                ) : (
                    <table className="table table-bordered mt-2 border-gray-300">
                        <tbody>
                            <tr>
                                <td className="border border-gray-300 py-3 text-xl text-center" colSpan="3">
                                    No Data available or Amount is null
                                </td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>

            <div className="text-md-right">
                <div className="d-flex justify-content-between">
                    <div className="ml-5">
                        <label htmlFor="">Jump to</label>
                        <select
                            className="mx-2"
                            style={{ width: "50px", height: "30px" }}
                            value={postsPerPage}
                            onChange={(e) => {
                                setPostsPerPage(parseInt(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={500}>500</option>
                        </select>
                        <span>Page {currentPage} of {howManyPages}</span>
                    </div>
                    <div className="mx-5 mb-5">
                        <Pagination pages={howManyPages} setCurrentPage={setCurrentPage} />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Charges;
